import { useEffect, useState } from 'react';
import { useAuth } from 'oidc-react';
import { DashboardTenant, getDashboardTenantsForGlobalAdmin } from '../../components/services/TenantSettingsService';

export interface ActivityLogTenants {
  /** The signed-in user's own tenant (token tenant_id), when it is in the list. */
  loggedInTenant: DashboardTenant | null;
  /** Every other tenant the user can see, sorted by name. */
  associatedTenants: DashboardTenant[];
  /** tenantId (lowercase) -> name, for labelling rows in the cross-tenant view. */
  namesById: Record<string, string>;
  loading: boolean;
  failed: boolean;
}

const EMPTY: ActivityLogTenants = { loggedInTenant: null, associatedTenants: [], namesById: {}, loading: false, failed: false };

/**
 * Tenants for the Scope dropdown, from Tenant Management's API (`tenantservice/tenant/dashboard/tenants`, the same
 * call the Customers page makes). TenantAPI scopes it by role: Globaladmin gets every tenant, a reseller its own
 * tenant plus its mapped customer tenants. Only loaded when `enabled` (roles that get the dropdown).
 */
export const useActivityLogTenants = (enabled: boolean): ActivityLogTenants => {
  const auth = useAuth();
  const ownTenantId = String(auth?.userData?.profile?.tenant_id ?? '').toLowerCase();
  const [state, setState] = useState<ActivityLogTenants>(enabled ? { ...EMPTY, loading: true } : EMPTY);

  useEffect(() => {
    if (!enabled) {
      setState(EMPTY);
      return;
    }

    let cancelled = false;
    setState({ ...EMPTY, loading: true });
    getDashboardTenantsForGlobalAdmin()
      .then((tenants) => {
        if (cancelled) return;
        const normalized = tenants.map((t) => ({ ...t, tenantId: String(t.tenantId).toLowerCase() }));
        const namesById = Object.fromEntries(normalized.map((t) => [t.tenantId, t.tenantName]));
        setState({
          loggedInTenant: normalized.find((t) => t.tenantId === ownTenantId) ?? null,
          associatedTenants: normalized
            .filter((t) => t.tenantId !== ownTenantId)
            .sort((a, b) => (a.tenantName || '').localeCompare(b.tenantName || '', undefined, { sensitivity: 'base' })),
          namesById,
          loading: false,
          failed: false,
        });
      })
      .catch(() => {
        // The shared API interceptor has already shown the error; the dropdown falls back to the header's tenant.
        if (!cancelled) setState({ ...EMPTY, failed: true });
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, ownTenantId]);

  return state;
};

export default useActivityLogTenants;
